<template>
  <div class="ProjectActionPlan" v-if="hasSimplePermissions('viewProjectOverview')">
    <div class="ProjectActionPlan--header">
      <h1>{{ $t('Late Parts') }}</h1>
      <div class="sub-header">
        <div class="ProjectActionPlan--info">
          {{ $t('Total for project') }}
          <span>{{ donePartCount }} / {{ partCount }}</span>
        </div>
        <span style="display: flex; float: right; white-space: nowrap">
          <div class="right-item mr-4">
            <label class="mr-1" for="lateControls">{{ $t('Show late parts') }}</label>
            <OnOff id="lateControls" v-model="showLateControlsControl"></OnOff>
          </div>
          <div class="right-item mr-4">
            <label class="mr-1" for="lateDeliveries">{{ $t('Show late deliveries') }}</label>
            <OnOff id="lateDeliveries" v-model="showLateDeliveriesControl"></OnOff>
          </div>
          <div class="right-item">
            <label class="mr-1" for="onlyMine" v-if="this.loggedInPerson">{{ $t('Only my stages') }}</label>
            <OnOff id="onlyMine" v-model="onlyMine" v-if="this.loggedInPerson" />
          </div>
        </span>
      </div>
    </div>
    <table class="alerts">
      <transition-group name="list" tag="thead">
        <tr v-for="(treeChild, index) in alertParts" :key="getPartBreadcrumb(treeChild) + treeChild._id">
          <td class="stagecolumn">
            {{ getPartBreadcrumb(treeChild) }}
          </td>
          <td class="titlecolumn">
            <router-link
              :to="{ name: 'ProjectPlanPart', params: { _id: treeChild.part.projectId, partId: treeChild._id } }"
            >
              {{ treeChild.title }}
            </router-link>
          </td>
          <td class="commentcolumn">
            <input
              class="commentText"
              :value="treeChild.part.text"
              :placeholder="$t('Add comment')"
              @change="updateSimpleProperty(treeChild.part, 'text', $event.target.value)"
            />
          </td>
          <td class="timecolumn">
            {{ timeColumn(treeChild.lateTime) }}
          </td>
          <td class="actionColumn">
            <div class="ctxMenu" tabindex="9">
              <div :class="treeChild.alert.css">
                <span v-if="isPostPoned(treeChild.part)">
                  {{ $t('Working on Solution') }}
                </span>
                <span v-else>
                  {{ $t('Action required') }}
                </span>
              </div>
              <div class="actionMenu">
                <div
                  :disabled="!hasPropertyPermissions('Part', 'controldate')"
                  @click="markLate(treeChild.part)"
                  class="popupAction late"
                  v-close-popover
                  v-if="!isLate(treeChild.part) && !isLateDelivery(treeChild.part)"
                >
                  {{ $t('Late') }}
                </div>
                <div
                  :disabled="!hasPropertyPermissions('Part', 'controldate')"
                  @click="postPoneControl(treeChild.part)"
                  class="popupAction postpone"
                  v-close-popover
                  v-if="!isPostPoned(treeChild.part) && !isLateDelivery(treeChild.part)"
                >
                  {{ $t('Action In Progress') }}
                </div>
                <div
                  class="popupAction fulfill"
                  v-close-popover
                  @click="controlFulfilled(treeChild.part)"
                  :disabled="!hasPropertyPermissions('Part', 'controlFulfilled')"
                  v-if="!isLateDelivery(treeChild.part)"
                >
                  {{ $t('Control Fulfilled') }}
                </div>
                <div
                  class="popupAction fulfill"
                  v-close-popover
                  @click="updateSimpleProperty(treeChild.part, 'deliveryFulfilled', true)"
                  :disabled="!hasPropertyPermissions('Part', 'deliveryFulfilled')"
                  v-if="isLateDelivery(treeChild.part)"
                >
                  {{ $t('Delivery Arrived') }}
                </div>
              </div>
            </div>
          </td>
        </tr>
      </transition-group>
    </table>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import { lateFuzzytime } from '@/common/timeHelpers';
import * as partHelpers from '@/common/partHelpers';
import OnOff from '@/components/switch/switch';

export default {
  metaInfo() {
    return {
      title: this.$t('Action Plan'),
    };
  },
  data() {
    return {
      onlyMine: false,
      showLateControls: true,
      showLateDeliveries: true,
    };
  },
  methods: {
    ...mapActions(['loadProject', 'savePartProperties']),
    markLate: function (part) {
      this.updateSimpleProperty(part, 'controldate', moment().format('YYYY-MM-DD'));
    },
    postPoneControl: function (part) {
      this.updateSimpleProperty(part, 'controldate', moment().add(4, 'day').format('YYYY-MM-DD'));
    },
    controlFulfilled: function (part) {
      this.updateSimpleProperty(part, 'controlFulfilled', true);
    },
    updateSimpleProperty: function (part, property, newValue) {
      if (part[property] !== newValue) {
        var newPartProperties = {};
        newPartProperties[property] = newValue;
        this.savePartProperties({ updatePart: part, newPartProperties });
      }
    },
    deliveryWarning: function (part) {
      return (
        part.deliverydate !== '0000-01-01' && moment(part.deliverydate).isBefore(moment()) && !part.deliveryFulfilled
      );
    },
    controlWarning: function (part) {
      return moment(part.controldate).isBefore(moment()) && !part.controlFulfilled;
    },
    getPartBreadcrumb: function (treeItem) {
      return treeItem.breadCrumbs.join(' → ');
    },
    timeColumn: function (lateTime) {
      if (lateTime === '') {
        return '';
      }
      var time = lateFuzzytime(lateTime);
      return this.$t(time.text, time);
    },
    isLateDelivery: function (part) {
      return partHelpers.isLateDelivery(part);
    },
    isPostPoned: function (part) {
      return partHelpers.isPostPoned(part);
    },
    isLate: function (part) {
      return partHelpers.isLate(part);
    },
    alertReason: function (part) {
      return partHelpers.partAlertReason(part);
    },
  },
  computed: {
    ...mapGetters([
      'currentProject',
      'donePartCount',
      'flattenFilteredParts',
      'hasPropertyPermissions',
      'hasSimplePermissions',
      'loggedInPerson',
      'partCount',
    ]),
    alertParts: function () {
      var filter = {};
      if (this.onlyMine) {
        filter.partManagerId = this.loggedInPerson._id;
      }
      if (this.showLateDeliveries && this.showLateControls) {
        filter.filterAlerts = true;
      } else if (this.showLateDeliveries) {
        filter.filterLateDeliveries = true;
      } else {
        filter.filterLate = true;
      }
      return this.flattenFilteredParts(filter);
    },
    showLateControlsControl: {
      get: function () {
        return this.showLateControls;
      },
      set: function (newValue) {
        this.showLateControls = newValue;
        if (!this.showLateDeliveries && !newValue) {
          this.showLateDeliveries = true;
        }
      },
    },
    showLateDeliveriesControl: {
      get: function () {
        return this.showLateDeliveries;
      },
      set: function (newValue) {
        this.showLateDeliveries = newValue;
        if (!this.showLateControls && !newValue) {
          this.showLateControls = true;
        }
      },
    },
  },
  components: {
    OnOff,
  },
  watch: {
    $route(to, from) {
      console.debug('ProjectActionPlan:watch');
      this.loadProject(this.$route.params._id);
    },
  },
};
</script>

<style scoped>
.ProjectActionPlan {
  padding: 0 20px;
}

.ProjectActionPlan--header {
  margin: 10px 0;
  padding: 10px 0;
  border-bottom: 3px solid #34414d;
  display: flex;
  flex-direction: column;
}

.ProjectActionPlan--header h1 {
  text-transform: uppercase;
}

.sub-header {
  display: flex;
  justify-content: space-between;
}

.ProjectActionPlan--info {
  color: #8c8d8c;
  display: inline-block;
  text-transform: uppercase;
}

.ProjectActionPlan--info > span {
  color: #e8963a;
}

table {
  font-size: 0.8em;
  width: 100%;
  border-collapse: collapse;
}

table tr:not(:first-child) {
  border-top: 1px solid #34414d;
  border-bottom: 1px solid #34414d;
}

table tr td {
  padding: 0.2em 0.5em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 0;
  width: 18%;
  text-align: left;
}

table tr td:first-of-type {
  width: 32%;
}
table tr td:nth-of-type(3) {
  width: 24%;
}

table tr td:nth-of-type(4) {
  width: 12%;
}

.stagecolumn,
.commentText {
  color: #b1b1b1;
  background: transparent;
}

.stagecolumn {
  direction: rtl;
  text-align: left;
}

.commentText {
  color: gray;
  background: transparent;
}

.actionColumn {
  cursor: pointer;
  text-align: right;
  overflow: visible;
}

.warning,
.error {
  border-radius: 20px;
  padding: 4px 10px;
  white-space: nowrap;
  min-width: 100px;
  text-align: center;
}

.error {
  background-color: #d44841;
}

.warning {
  background-color: #e8963a;
}

.popupAction {
  cursor: pointer;
}

.popupAction.late:hover {
  background-color: #d44841;
}

.popupAction.postpone:hover {
  background-color: #e8963a;
}

.popupAction.fulfill:hover {
  background-color: #29af8f;
}
</style>
<style>
.ctxMenu {
  position: relative;
  outline: none;
  display: inline-block;
}
.ctxMenu:focus .actionMenu {
  display: block;
}
.actionMenu {
  display: none;
  position: absolute;
  z-index: 9;
}
.actionMenu .popupAction {
  background: #34363d;
  padding: 5px;
  font-size: 0.875em;
}

.actionMenu .popupAction:first-of-type {
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
}

.actionMenu .popupAction:last-of-type {
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
}

.ctxMenu .ctxMenu-arrow {
  width: 0;
  height: 0;
  border-style: solid;
  position: absolute;
  margin: 5px;
  border-color: #34363d;
  z-index: 1;
}

.ctxMenu[x-placement^='top'] {
  margin-bottom: 10px;
}

.ctxMenu[x-placement^='top'] .ctxMenu-arrow {
  border-width: 8px 8px 0 8px;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  bottom: -8px;
  left: 120px;
  margin-top: 0;
  margin-bottom: 0;
}

.ctxMenu[x-placement^='bottom'] {
  margin-top: 10px;
}

.ctxMenu[x-placement^='bottom'] .ctxMenu-arrow {
  border-width: 0 8px 8px 8px;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-top-color: transparent !important;
  top: -8px;
  left: 85px;
  margin-top: 0;
  margin-bottom: 0;
}

.ctxMenu[x-placement^='right'] {
  margin-left: 5px;
}

.ctxMenu[x-placement^='right'] .ctxMenu-arrow {
  border-width: 5px 5px 5px 0;
  border-left-color: transparent !important;
  border-top-color: transparent !important;
  border-bottom-color: transparent !important;
  left: -5px;
  top: calc(50% - 5px);
  margin-left: 0;
  margin-right: 0;
}

.ctxMenu[x-placement^='left'] {
  margin-right: 5px;
}

.ctxMenu[x-placement^='left'] .ctxMenu-arrow {
  border-width: 5px 0 5px 5px;
  border-top-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  right: -5px;
  top: calc(50% - 5px);
  margin-left: 0;
  margin-right: 0;
}

@media (max-width: 1280px) {
  .sub-header {
    flex-direction: column;
  }
}
@media (max-width: 1024px) {
  .sub-header span {
    flex-direction: column;
  }
  .sub-header .right-item {
    display: flex;
    justify-content: space-between;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/projectoverview/ProjectActionPlan.vue